import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  Space,
  Tooltip,
  Select,
  message,
  Switch,
  Divider,
  Typography,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import lang from "../helper/lang";
import Main from "../components/layout/Main";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import useDebounce from "../hooks/useDebounce";
import apiPath from "../constants/apiPath";
import LocationPicker from "../components/LocationPicker";
import PhoneInput from "react-phone-input-2";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { getExampleNumber } from "libphonenumber-js/max";
import examples from "libphonenumber-js/examples.mobile.json";

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

function LabManager() {
  const { request } = useRequest();
  const isMounted = useRef(false);
  const requestRef = useRef(request);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // State management
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLab, setSelectedLab] = useState(null);
  const [countryCode, setCountryCode] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState("add"); // add, edit
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const { RangePicker } = DatePicker;
  const [filters, setFilters] = useState({
    sort: "newest",
    status: null,
    dateRange: null,
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [phoneCountryIso, setPhoneCountryIso] = useState(null);

  // Map state
  const [mapCenter, setMapCenter] = useState({ lat: 25.0, lng: 71.0 }); // Default to Saudi Arabia
  const [markerPosition, setMarkerPosition] = useState({
    lat: 25.0,
    lng: 71.0,
  });

  // Memoize initialPosition to prevent unnecessary re-renders of LocationPicker
  const initialPosition = useMemo(() => {
    if (modalType === "edit" && selectedLab) {
      return {
        lat: parseFloat(selectedLab.location?.latitude) || 25.0,
        lng: parseFloat(selectedLab.location?.longitude) || 71.0,
      };
    }
    return null;
  }, [modalType, selectedLab]);

  // Loading states for different operations
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});

  const debouncedSearchText = useDebounce(searchText, 500);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  // Initialize Google Map
  const initMap = useCallback((lat = 25.0, lng = 71.0) => {
    // This function will be called when the map modal opens
    // We'll implement the actual map initialization in the modal
  }, []);

  // Handle map location selection
  const handleLocationSelect = useCallback(
    (locationData) => {
      const { lat, lng, city } = locationData;
      setMarkerPosition({ lat, lng });

      // Update form fields with real-time values
      form.setFieldsValue({
        latitude: parseFloat(lat.toFixed(6)),
        longitude: parseFloat(lng.toFixed(6)),
        city: city || `Location at ${lat.toFixed(4)}, ${lng.toFixed(4)}`,
      });
    },
    [form],
  );

  // Reverse geocode to get city name from coordinates
  // const reverseGeocode = useCallback(
  //   async (lat, lng) => {
  //     if (!lat || !lng) return;

  //     try {
  //       const response = await fetch(
  //         `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=YOUR_GOOGLE_MAPS_API_KEY`
  //       );

  //       const data = await response.json();

  //       if (data.status === "OK" && data.results.length > 0) {
  //         const addressComponents = data.results[0].address_components;

  //         const cityComponent =
  //           addressComponents.find((c) =>
  //             c.types.includes("locality")
  //           ) ||
  //           addressComponents.find((c) =>
  //             c.types.includes("administrative_area_level_2")
  //           );

  //         const cityName = cityComponent?.long_name || "";

  //         form.setFieldsValue({ city: cityName });
  //       }
  //     } catch (error) {
  //       console.error("Reverse geocoding failed:", error);
  //     }
  //   },
  //   [form]
  // );

  // Fetch labs
  const fetchLabs = useCallback(() => {
    if (!isMounted.current) return;

    setLoading(true);
    const params = new URLSearchParams();

    // Add sort parameter
    if (filters.sort) {
      params.append("sort", filters.sort);
    }

    // Add status parameter
    if (filters.status) {
      params.append("status", filters.status);
    }

    // Add search parameter
    if (debouncedSearchText) {
      params.append("search", debouncedSearchText);
    }

    // Add date range filter
    if (filters.dateRange?.length === 2) {
      params.append("start_date", filters.dateRange[0].format("YYYY-MM-DD"));
      params.append("end_date", filters.dateRange[1].format("YYYY-MM-DD"));
    }

    // Add pagination parameters
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    requestRef.current({
      url: `/api/admin/${apiPath.labs}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        if (!isMounted.current) return;

        setLoading(false);

        if (data?.status === 200 && data?.data) {
          setLabs(Array.isArray(data.data) ? data.data : []);

          // Handle pagination from your API response
          if (data.pagination) {
            setPagination((prev) => ({
              ...prev,
              total: data.pagination.total || 0,
              current: data.pagination.page || prev.current,
              pageSize: data.pagination.limit || prev.pageSize,
            }));
          }
        } else {
          setLabs([]);
          setPagination((prev) => ({ ...prev, total: 0 }));
          ShowToast(data?.message || "Failed to fetch labs", Severty.ERROR);
        }
      },
      onError: (error) => {
        if (!isMounted.current) return;

        setLoading(false);
        setLabs([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        ShowToast("Failed to fetch labs", Severty.ERROR);
      },
    });
  }, [
    filters.sort,
    filters.status,
    filters.dateRange,
    debouncedSearchText,
    pagination.current,
    pagination.pageSize,
  ]);

  useEffect(() => {
    fetchLabs();
  }, [
    filters.sort,
    filters.status,
    filters.dateRange,
    debouncedSearchText,
    pagination.current,
    pagination.pageSize,
  ]);

  // Handlers
  const handleTableChange = (paginationInfo, filters, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleAddLab = () => {
    setModalType("add");
    form.resetFields();
    setMarkerPosition({ lat: 25.0, lng: 71.0 });
    form.setFieldsValue({
      latitude: 25.0,
      longitude: 71.0,
      city: "",
    });
    setModalVisible(true);
  };

  const handleEditLab = (lab) => {
    setSelectedLab(lab);
    setModalType("edit");
    setModalVisible(true);

    const lat = parseFloat(lab.location?.latitude);
    const lng = parseFloat(lab.location?.longitude);

    // Set form values for editing
    form.setFieldsValue({
      lab_name: lab.name,
      contact_name: lab.contact_person?.full_name,
      email: lab.contact_person?.email,
      mobile: `${lab.contact_person?.country_code}${lab.contact_person?.mobile}`,
      country_code: lab.contact_person.country_code,
      city: lab.location?.city || "",
      latitude: lat,
      longitude: lng,
      status: lab.status === "ACTIVE",
    });

    setMarkerPosition({ lat, lng });
  };

  // Create or update lab
  const handleFormSubmit = async (values) => {
    try {
      // 🔒 FINAL mobile normalization (single source of truth)
      const rawMobile = values.mobile || "";

      // remove everything except digits
      const digitsOnly = rawMobile.replace(/\D/g, "");

      // remove country code ONLY if it exists at start
      const cleanMobile =
        countryCode && digitsOnly.startsWith(countryCode)
          ? digitsOnly.slice(countryCode.length)
          : digitsOnly;

      const labData = {
        lab_name: values.lab_name,
        contact_name: values.contact_name,
        email: values.email,
        country_code: countryCode,
        mobile: cleanMobile,
        city: values.city,
        latitude: parseFloat(values.latitude),
        longitude: parseFloat(values.longitude),
        status: values.status ? "ACTIVE" : "INACTIVE",
      };

      if (modalType === "add") {
        setCreateLoading(true);
        console.log("payload:", labData);
        requestRef.current({
          url: `/api/admin/${apiPath.labs}`,
          method: "POST",
          data: labData,
          onSuccess: (response) => {
            if (!isMounted.current) return;

            if (response?.status === 201) {
              ShowToast(
                response?.data?.message || "Lab created successfully",
                Severty.SUCCESS,
              );
              setModalVisible(false);
              setPhoneCountryIso(null);
              fetchLabs();
            } else {
              ShowToast(
                response?.data?.message || "Failed to create lab",
                Severty.ERROR,
              );
              setPhoneCountryIso(null);
            }
            setCreateLoading(false);
            setPhoneCountryIso(null);
          },
          onError: (error) => {
            if (!isMounted.current) return;
            ShowToast(
              error?.response?.data?.message || "Failed to create lab",
              Severty.ERROR,
            );
            setCreateLoading(false);
            setPhoneCountryIso(null);
          },
        });
      } else {
        setUpdateLoading(true);
        requestRef.current({
          url: `/api/admin/${apiPath.labs}/${selectedLab.id}`,
          method: "PUT",
          data: labData,
          onSuccess: (data) => {
            if (!isMounted.current) return;

            if (data?.status === 200) {
              ShowToast("Lab updated successfully", Severty.SUCCESS);
              setModalVisible(false);
              fetchLabs();
            } else {
              ShowToast(data?.message || "Failed to update lab", Severty.ERROR);
            }
            setUpdateLoading(false);
          },
          onError: (error) => {
            if (!isMounted.current) return;

            ShowToast("Failed to update lab", Severty.ERROR);
            setUpdateLoading(false);
          },
        });
      }
    } catch (error) {
      ShowToast("Failed to save lab", Severty.ERROR);
      setCreateLoading(false);
      setUpdateLoading(false);
      setPhoneCountryIso(null);
    }
  };

  const isValidPhoneNumber = (phone, countryCode) => {
    if (!phone || !countryCode) return false;

    const phoneNumber = parsePhoneNumberFromString(
      phone,
      countryCode.toUpperCase(),
    );

    return phoneNumber ? phoneNumber.isValid() : false;
  };

  const getPhoneExample = (countryCode) => {
    console.log("country_code==>", countryCode)
    try {
      const example = getExampleNumber(countryCode, examples);
      if (!example) return null;

      const intl = example.formatInternational(); 
      // "+91 62654 53127"

      // keep + and spaces, mask digits except country code
      const countryCallingCode = example.countryCallingCode;

      let seenCountryCode = false;

      return intl.replace(/\d+/g, (match) => {
        // first numeric chunk is country calling code
        if (!seenCountryCode) {
          seenCountryCode = true;
          return match; // keep country code
        }
        return "x".repeat(match.length);
      });
    } catch {
      return null;
    }
  };

  const handleDeleteLab = async (lab) => {
    try {
      setDeleteLoading((prev) => ({ ...prev, [lab.id]: true }));
      requestRef.current({
        url: `/api/admin/${apiPath.labs}/${lab.id}`,
        method: "DELETE",
        onSuccess: (data) => {
          if (!isMounted.current) return;

          ShowToast("Lab deleted successfully", Severty.SUCCESS);
          setDeleteLoading((prev) => ({ ...prev, [lab.id]: false }));
          fetchLabs();
        },
        onError: () => {
          if (!isMounted.current) return;

          ShowToast("Failed to delete lab", Severty.ERROR);
          setDeleteLoading((prev) => ({ ...prev, [lab.id]: false }));
        },
      });
    } catch (error) {
      ShowToast("Failed to delete lab", Severty.ERROR);
      setDeleteLoading((prev) => ({ ...prev, [lab.id]: false }));
    }
  };

  // Table columns
  const getColumns = () => [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return <Text>{serialNumber}</Text>;
      },
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (id) => <Text code>LAB_{id}</Text>,
    },
    {
      title: "Lab Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name) => <strong>{name}</strong>,
    },
    {
      title: "City",
      dataIndex: ["location", "city"],
      key: "city",
      render: (city) => <span>{city || "N/A"}</span>,
    },
    {
      title: "Contact Person",
      dataIndex: ["contact_person", "full_name"],
      key: "contact_person",
      render: (name) => <span>{name || "N/A"}</span>,
    },
    {
      title: "Email",
      dataIndex: ["contact_person", "email"],
      key: "email",
      render: (email) => <span>{email || "N/A"}</span>,
    },
    {
      title: "Mobile",
      dataIndex: ["contact_person", "mobile"],
      key: "mobile",
      render: (mobile, record) => (
        <span>
          {`${record.contact_person.country_code}-${mobile}` || "N/A"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color={status === "ACTIVE" ? "green" : "red"}>{status}</Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 200,
      sorter: (a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateA - dateB;
      },
      render: (date) => {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span>{new Date(date).toLocaleString() || "N/A"}</span>
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEditLab(record)}
              disabled={deleteLoading[record.id]}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              icon={<DeleteOutlined />}
              danger
              loading={deleteLoading[record.id]}
              onClick={() => {
                Modal.confirm({
                  title: "Delete Lab",
                  content: "Are you sure you want to delete this lab?",
                  okText: "Yes",
                  okType: "danger",
                  cancelText: "No",
                  onOk: () => handleDeleteLab(record),
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <>
            <span>Lab Manager</span>
            <p>Manage diagnostic labs in the system</p>
          </>
        }
        extra={
          <Space wrap>
            <Search
              placeholder="Search labs"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Sort By"
              style={{ width: 120 }}
              value={filters.sort}
              onChange={(value) => handleFilterChange("sort", value)}
            >
              <Option value="newest">Newest</Option>
              <Option value="oldest">Oldest</Option>
            </Select>
            <RangePicker
              style={{ width: 300 }}
              placeholder={["Start Date", "End Date"]}
              value={filters.dateRange}
              onChange={(dates) => handleFilterChange("dateRange", dates)}
              allowClear
            />
            <Select
              placeholder="Filter by Status"
              style={{ width: 150 }}
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange("status", value)}
            >
              <Option value="ACTIVE">Active</Option>
              <Option value="INACTIVE">Inactive</Option>
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddLab}
            >
              Add Lab
            </Button>
          </Space>
        }
      >
        {/* Labs Table */}
        <div className="table-responsive">
          <Table
            columns={getColumns()}
            dataSource={labs}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total || 0,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} labs`,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>
      </Card>

      {/* Add/Edit Lab Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {modalType === "add" ? "Add Lab" : "Edit Lab"}
          </div>
        }
        open={modalVisible}
        onCancel={() =>
          !createLoading && !updateLoading && setModalVisible(false)
        }
        footer={null}
        width={700}
        confirmLoading={createLoading || updateLoading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            try {
              await form.validateFields(); // 👈 FORCE validation
              handleFormSubmit(values);
            } catch (err) {
              // validation failed — do nothing
            }
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="lab_name"
                label="Lab Name"
                rules={[
                  {
                    required: true,
                    message: "Please enter lab name",
                  },
                  {
                    max: 100,
                    message: "Lab name must be 100 characters or less",
                  },
                  {
                    pattern: /^[A-Za-z0-9 _-]+$/,
                    message: "Special characters are not allowed",
                  },
                ]}
              >
                <Input placeholder="Enter lab name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contact_name"
                label="Contact Person Name"
                rules={[
                  {
                    required: true,
                    message: "Please enter contact person name",
                  },
                  {
                    max: 100,
                    message: "Contact name must be 100 characters or less",
                  },
                  {
                    pattern: /^[A-Za-z0-9 _-]+$/,
                    message: "Special characters are not allowed",
                  },
                ]}
              >
                <Input placeholder="Enter contact person name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  {
                    required: true,
                    message: lang("Please_enter_email") || "Please enter email",
                  },
                  {
                    validator(_, value) {
                      if (!value) return Promise.resolve();

                      // ❌ no starting dot
                      // ❌ no ending dot
                      // ❌ no consecutive dots
                      // ✅ valid domain & TLD
                      const emailRegex =
                        /^(?!\.)(?!.*\.\.)[a-zA-Z0-9._%+-]+(?<!\.)@[a-zA-Z0-9-]+\.[a-zA-Z]{2,24}$/;

                      if (!emailRegex.test(value)) {
                        return Promise.reject(
                          new Error(
                            lang("Please_enter_valid_email") ||
                              "Please enter a valid email address",
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="Enter email" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="mobile"
                label="Contact Person Mobile"
                help={
                  phoneCountryIso
                    ? `Example: ${getPhoneExample(phoneCountryIso)}`
                    : null
                }
                rules={[
                  { required: true, message: "Please enter your phone number" },
                  () => ({
                    validator(_, value) {
                      if (!value || !phoneCountryIso) return Promise.resolve();

                      // remove non-digits
                      const digitsOnly = value.replace(/\D/g, "");

                      // countryCode is like "91", "1", etc.
                      const localNumber = countryCode
                        ? digitsOnly.replace(new RegExp(`^${countryCode}`), "")
                        : digitsOnly;

                      if (!/^\d+$/.test(localNumber)) {
                        return Promise.reject(
                          new Error("Phone number must contain only digits"),
                        );
                      }

                      // validate ONLY local mobile number
                      const valid = isValidPhoneNumber(value, phoneCountryIso);

                      return valid
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error("Invalid phone number for selected country")
                          );
                    },
                  }),
                ]}
              >
                <PhoneInput
                  country="in"
                  enableSearch
                  value={form.getFieldValue("mobile")}
                  onChange={(value, country) => {
                    const cleanedValue = value.replace(/\+/g, "");
                    setCountryCode(country.dialCode); // 👈 country code stored separately
                    setPhoneCountryIso(country.countryCode.toUpperCase());
                    form.setFieldsValue({ mobile: cleanedValue });
                  }}
                  inputStyle={{
                    width: "100%",
                    height: "50px",
                    borderRadius: "12px",
                    background: "transparent",
                    color: "white",
                    border: "1px solid #d9d9d9",
                    fontSize: "16px",
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="city"
                label="City"
                rules={[
                  {
                    required: true,
                    message: "Please enter city",
                  },
                  {
                    pattern: /^[A-Za-z0-9 _-]+$/,
                    message: "Special characters are not allowed",
                  },
                ]}
              >
                <Input placeholder="Enter city" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Location</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="latitude"
                label="Latitude"
                rules={[
                  {
                    required: true,
                    message: "Please enter latitude",
                  },
                ]}
              >
                <Input type="number" step="any" placeholder="Enter latitude" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="longitude"
                label="Longitude"
                rules={[
                  {
                    required: true,
                    message: "Please enter longitude",
                  },
                ]}
              >
                <Input type="number" step="any" placeholder="Enter longitude" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Select Location on Map">
            <LocationPicker
              onLocationSelect={handleLocationSelect}
              initialPosition={initialPosition}
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={() => {
                setModalVisible(false);
                setPhoneCountryIso(null);
              }}
              disabled={createLoading || updateLoading}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={modalType === "add" ? createLoading : updateLoading}
              disabled={createLoading || updateLoading}
            >
              {modalType === "add" ? "Create" : "Update"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default LabManager;
